import { useCallback, useEffect, useRef, useState } from 'react';
import { Roles } from '@touristation/shared';
import { useAuth } from './auth/AuthProvider';
import { useLang } from './i18n/LangProvider';
import {
  useInfostaff,
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
} from './api/hooks';
import { Icon, Brand, type IconName } from './ui/Icon';
import { fmtDate } from './ui/atoms';
import { CommandPalette, type Destination } from './ui/CommandPalette';
import { LoginScreen } from './screens/LoginScreen';
import { AuthorizeScreen } from './screens/AuthorizeScreen';
import { ResetPasswordScreen } from './screens/ResetPasswordScreen';
import { HubScreen } from './screens/HubScreen';
import { ToolsScreen } from './screens/ToolsScreen';
import { InfostaffScreen } from './screens/InfostaffScreen';
import { NotesScreen } from './screens/NotesScreen';
import { WikiScreen } from './screens/WikiScreen';
import { LearningScreen } from './screens/LearningScreen';
import { EmailsScreen } from './screens/EmailsScreen';
import { TasksScreen } from './screens/TasksScreen';
import { ShiftsScreen } from './screens/ShiftsScreen';
import { CalendarScreen } from './screens/CalendarScreen';
import { LeaveScreen } from './screens/LeaveScreen';
import { HandoverScreen } from './screens/HandoverScreen';
import { AdminScreen } from './screens/AdminScreen';
import { ProfileScreen } from './screens/ProfileScreen';
import { SettingsScreen } from './screens/SettingsScreen';

type Page =
  | 'hub' | 'tools'
  | 'infostaff' | 'wiki' | 'learning' | 'notes'
  | 'emails' | 'tasks' | 'shifts' | 'calendar' | 'leave' | 'handover'
  | 'admin' | 'profile' | 'settings';

const PAGES = new Set<Page>([
  'hub', 'tools', 'infostaff', 'wiki', 'learning', 'notes',
  'emails', 'tasks', 'shifts', 'calendar', 'leave', 'handover',
  'admin', 'profile', 'settings',
]);

function pageFromLocation(): Page | null {
  const raw = window.location.hash.slice(1);
  if (!raw) return 'hub';
  const candidate = raw as Page;
  return PAGES.has(candidate) ? candidate : null;
}

interface NavItem {
  id: Page;
  label: string;
  icon: IconName;
  section?: string;
  adminOnly?: boolean;
  count?: number;
  urgent?: boolean;
}

/** Page → topbar title + breadcrumb. */
const TITLES: Record<Page, { t: string; c: string }> = {
  hub: { t: 'Hub', c: 'Panoramica' },
  tools: { t: 'Tool & programmi', c: 'Operatività' },
  infostaff: { t: 'Infostaff', c: 'Comunicazioni' },
  emails: { t: 'Mail aziendali', c: 'Rubrica' },
  wiki: { t: 'Wiki aziendale', c: 'Knowledge base' },
  learning: { t: 'Formazione', c: 'Formazione' },
  notes: { t: 'Appunti personali', c: 'Personale' },
  tasks: { t: 'Task', c: 'Organizzazione' },
  shifts: { t: 'Turni', c: 'Organizzazione' },
  calendar: { t: 'Calendario', c: 'Organizzazione' },
  leave: { t: 'Ferie e permessi', c: 'Organizzazione' },
  handover: { t: 'Consegne di fine giornata', c: 'Organizzazione' },
  admin: { t: 'Dashboard Admin', c: 'Amministrazione' },
  profile: { t: 'Il mio profilo', c: 'Account' },
  settings: { t: 'Impostazioni', c: 'Account' },
};

/** Destinations offered by the ⌘K command palette (crumb comes from TITLES). */
const COMMAND_DESTINATIONS: { id: Page; icon: IconName; adminOnly?: boolean }[] = [
  { id: 'hub', icon: 'home' },
  { id: 'tools', icon: 'tools' },
  { id: 'infostaff', icon: 'bell' },
  { id: 'emails', icon: 'mail' },
  { id: 'wiki', icon: 'book' },
  { id: 'learning', icon: 'cap' },
  { id: 'notes', icon: 'edit' },
  { id: 'tasks', icon: 'list' },
  { id: 'shifts', icon: 'calendar' },
  { id: 'calendar', icon: 'calendar' },
  { id: 'leave', icon: 'clock' },
  { id: 'handover', icon: 'send' },
  { id: 'admin', icon: 'shield', adminOnly: true },
  { id: 'profile', icon: 'edit' },
  { id: 'settings', icon: 'settings' },
];

function initialsOf(name: string, email: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  if (parts.length === 1 && parts[0]) return parts[0].slice(0, 2).toUpperCase();
  return (email[0] ?? '?').toUpperCase();
}

function Sidebar({
  active, onNav, isAdmin, mobileOpen, onCloseMobile,
  userName, userRole, userInitials, onOpenProfile, onOpenSettings,
}: {
  active: Page;
  onNav: (p: Page) => void;
  isAdmin: boolean;
  mobileOpen: boolean;
  onCloseMobile: () => void;
  userName: string;
  userRole: string;
  userInitials: string;
  onOpenProfile: () => void;
  onOpenSettings: () => void;
}) {
  const { t } = useLang();
  // This query lives here (not in App) so it only fires once authenticated.
  const infostaff = useInfostaff();
  const unreadInfo = infostaff.data?.items.filter((i) => !i.read).length ?? 0;

  const items: NavItem[] = [
    { id: 'hub', label: 'Hub', icon: 'home' },
    { id: 'tools', label: 'Tool & programmi', icon: 'tools', section: 'Operatività' },
    { id: 'infostaff', label: 'Infostaff', icon: 'bell', count: unreadInfo, urgent: unreadInfo > 0, section: 'Comunicazioni' },
    { id: 'emails', label: 'Mail aziendali', icon: 'mail', section: 'Rubrica' },
    { id: 'wiki', label: 'Wiki aziendale', icon: 'book', section: 'Knowledge base' },
    { id: 'learning', label: 'Formazione', icon: 'cap' },
    { id: 'notes', label: 'Appunti personali', icon: 'edit' },
    { id: 'tasks', label: 'Task', icon: 'list', section: 'Organizzazione' },
    { id: 'shifts', label: 'Turni', icon: 'calendar' },
    { id: 'calendar', label: 'Calendario', icon: 'calendar' },
    { id: 'leave', label: 'Ferie e permessi', icon: 'clock' },
    { id: 'handover', label: 'Consegne', icon: 'send' },
    { id: 'admin', label: 'Dashboard Admin', icon: 'shield', section: 'Amministrazione', adminOnly: true },
  ];

  let lastSection: string | null = null;
  return (
    <aside
      id="app-sidebar"
      className={`sidebar ${mobileOpen ? 'is-open' : ''}`}
      aria-label={t('Menu principale')}
      role={mobileOpen ? 'dialog' : undefined}
      aria-modal={mobileOpen ? 'true' : undefined}
    >
      <div className="sidebar__brand">
        <Brand size={32} />
        <div className="sidebar__brand-copy">
          <div className="sidebar__brand-name">Touristation</div>
          <div className="sidebar__brand-sub">Hub · Roma</div>
        </div>
        <button className="sidebar__close" onClick={onCloseMobile} aria-label={t('Chiudi menu')}>
          <Icon name="x" size={18} />
        </button>
      </div>

      <nav className="sidebar__nav">
        {items.map((it) => {
          if (it.adminOnly && !isAdmin) return null;
          const showSection = it.section && it.section !== lastSection;
          if (it.section) lastSection = it.section;
          return (
            <div key={it.id}>
              {showSection && <div className="nav-section">{t(it.section!)}</div>}
              <button
                className={`nav-item ${active === it.id ? 'is-active' : ''}`}
                onClick={() => { onNav(it.id); onCloseMobile(); }}
                aria-current={active === it.id ? 'page' : undefined}
              >
                <Icon name={it.icon} size={16} />
                <span className="nav-item__label">{t(it.label)}</span>
                {!!it.count && it.count > 0 && (
                  <span className={`nav-item__count ${it.urgent ? 'is-urgent' : ''}`}>{it.count}</span>
                )}
              </button>
            </div>
          );
        })}
      </nav>

      <div className="sidebar__user">
        <button className={`avatar ${active === 'profile' ? 'is-active' : ''}`} onClick={() => { onOpenProfile(); onCloseMobile(); }} title={t('Apri profilo')} style={{ cursor: 'pointer' }}>
          {userInitials}
        </button>
        <button className={`sidebar__user-info ${active === 'profile' ? 'is-active' : ''}`} onClick={() => { onOpenProfile(); onCloseMobile(); }} title={t('Apri profilo')}>
          <div className="user-name">{userName}</div>
          <div className="user-role">{userRole}</div>
        </button>
        <button className={`btn btn--ghost btn--icon ${active === 'settings' ? 'is-active' : ''}`} title={t('Impostazioni')} onClick={() => { onOpenSettings(); onCloseMobile(); }}>
          <Icon name="settings" size={15} />
        </button>
      </div>
    </aside>
  );
}

function Topbar({
  title, crumb, onMenu, menuOpen, onOpenSearch,
}: {
  title: string;
  crumb: string;
  onMenu: () => void;
  menuOpen: boolean;
  onOpenSearch: () => void;
}) {
  const { lang, setLang, t } = useLang();
  const [openBell, setOpenBell] = useState(false);
  const bellButtonRef = useRef<HTMLButtonElement>(null);
  const bellCloseRef = useRef<HTMLButtonElement>(null);
  const notifs = useNotifications();
  const markAll = useMarkAllNotificationsRead();
  const markRead = useMarkNotificationRead();
  const items = notifs.data?.items ?? [];
  const unread = items.filter((n) => !n.read).length;
  const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

  useEffect(() => {
    if (!openBell) return;
    const popover = document.getElementById('notifications-popover');
    const focusableSelector = 'button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])';
    const focusPopover = window.requestAnimationFrame(() => bellCloseRef.current?.focus());
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setOpenBell(false);
        return;
      }
      if (event.key !== 'Tab' || !popover) return;
      const focusable = Array.from(popover.querySelectorAll<HTMLElement>(focusableSelector))
        .filter((el) => el.offsetParent !== null);
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      window.cancelAnimationFrame(focusPopover);
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
      bellButtonRef.current?.focus();
    };
  }, [openBell]);

  return (
    <div className={`topbar ${openBell ? 'has-open-popover' : ''}`}>
      <button
        className="menu-toggle"
        onClick={onMenu}
        aria-label={t('Apri menu')}
        aria-expanded={menuOpen}
        aria-controls="app-sidebar"
      >
        <Icon name="menu" size={18} />
      </button>
      <div className="topbar__heading">
        <div className="topbar__crumb">{t(crumb)}</div>
        <div className="topbar__title">{t(title)}</div>
      </div>
      <div className="topbar__spacer" />
      <div className="topbar__actions">
        <button type="button" className="search" onClick={onOpenSearch} aria-label={t('Cerca e vai a una sezione')}>
          <Icon name="search" size={14} />
          <span className="search__placeholder">{t('Vai a una sezione…')}</span>
          <span className="kbd">{isMac ? '⌘K' : 'Ctrl K'}</span>
        </button>
        <div className="role-switch">
          <button className={lang === 'it' ? 'is-active' : ''} onClick={() => setLang('it')}>IT</button>
          <button className={lang === 'en' ? 'is-active' : ''} onClick={() => setLang('en')}>EN</button>
        </div>

        <div className="bell-wrap">
          <button
            ref={bellButtonRef}
            className="btn btn--ghost btn--icon bell-btn"
            onClick={() => setOpenBell((o) => !o)}
            aria-label={unread > 0 ? t('Notifiche, {n} non lette', { n: unread }) : t('Notifiche')}
            aria-expanded={openBell}
            aria-controls="notifications-popover"
          >
            <Icon name="bell" size={16} />
            {unread > 0 && <span className="bell-dot">{unread}</span>}
          </button>
          {openBell && (
            <>
              <div className="bell-backdrop" onClick={() => setOpenBell(false)} />
              <div
                id="notifications-popover"
                className="bell-pop"
                role="dialog"
                aria-modal="true"
                aria-label="Notifiche"
              >
                <div className="bell-pop__head">
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{t('Notifiche')}</div>
                    <div className="muted" style={{ fontSize: 11.5 }}>{t('{n} non lette', { n: unread })}</div>
                  </div>
                  <div className="bell-pop__actions">
                    {unread > 0 && (
                      <button className="btn btn--sm" onClick={() => markAll.mutate()}>{t('Segna tutte lette')}</button>
                    )}
                    <button ref={bellCloseRef} className="btn btn--ghost btn--icon bell-pop__close" onClick={() => setOpenBell(false)} aria-label={t('Chiudi notifiche')}>
                      <Icon name="x" size={16} />
                    </button>
                  </div>
                </div>
                <div className="bell-pop__list">
                  {items.length === 0 && <div className="empty">{t('Nessuna notifica.')}</div>}
                  {items.map((n) => (
                    <div
                      key={n.id}
                      className={`bell-item ${!n.read ? 'is-unread' : ''}`}
                      role={!n.read ? 'button' : undefined}
                      tabIndex={!n.read ? 0 : undefined}
                      onClick={!n.read ? () => markRead.mutate(n.id) : undefined}
                      onKeyDown={!n.read ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); markRead.mutate(n.id); } } : undefined}
                      title={!n.read ? t('Segna come letta') : undefined}
                      style={!n.read ? { cursor: 'pointer' } : undefined}
                    >
                      <div className="bell-item__ico" style={{ background: 'var(--t-purple)' }}>
                        <Icon name="bell" size={12} />
                      </div>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div className="bell-item__title">{n.title}</div>
                        <div className="bell-item__body">{n.body}</div>
                        <div className="bell-item__meta"><span>{fmtDate(n.createdAt)}</span></div>
                      </div>
                      {!n.read && <span className="bell-item__dot" />}
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export function App() {
  const auth = useAuth();
  const [page, setPage] = useState<Page>(() => pageFromLocation() ?? 'hub');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const drawerOpener = useRef<HTMLElement | null>(null);

  const navigate = useCallback((next: Page) => {
    setPage(next);
    setMobileOpen(false);
    if (window.location.hash === `#${next}`) {
      const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
      window.scrollTo({ top: 0, left: 0, behavior });
    } else {
      window.history.pushState({ page: next }, '', `#${next}`);
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }
  }, []);

  useEffect(() => {
    const onLocationChange = () => {
      const next = pageFromLocation();
      if (!next) return;
      setPage(next);
      setMobileOpen(false);
    };
    window.addEventListener('popstate', onLocationChange);
    window.addEventListener('hashchange', onLocationChange);
    return () => {
      window.removeEventListener('popstate', onLocationChange);
      window.removeEventListener('hashchange', onLocationChange);
    };
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [page]);

  // Global ⌘K / Ctrl+K opens the command palette. On the Wiki page ⌘K is that
  // screen's own page switcher, so we defer to it there.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k' && page !== 'wiki') {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [page]);

  useEffect(() => {
    if (
      auth.ready
      && auth.authenticated
      && page === 'admin'
      && !auth.roles.includes(Roles.ADMIN)
    ) {
      setPage('hub');
      window.history.replaceState({ page: 'hub' }, '', '#hub');
    }
  }, [auth.authenticated, auth.ready, auth.roles, page]);

  useEffect(() => {
    if (!mobileOpen) return;

    drawerOpener.current = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const drawer = document.getElementById('app-sidebar');
    const focusableSelector = 'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

    const focusDrawer = window.requestAnimationFrame(() => {
      drawer?.querySelector<HTMLElement>('.sidebar__close')?.focus();
    });
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setMobileOpen(false);
        return;
      }
      if (event.key !== 'Tab' || !drawer) return;
      const focusable = Array.from(drawer.querySelectorAll<HTMLElement>(focusableSelector))
        .filter((el) => el.offsetParent !== null);
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);

    return () => {
      window.cancelAnimationFrame(focusDrawer);
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
      const opener = drawerOpener.current;
      if (opener?.isConnected) window.requestAnimationFrame(() => opener.focus());
    };
  }, [mobileOpen]);

  // OIDC authorization endpoint — the "sign in with Touristation" landing page.
  // Handled before the normal shell so an active session flows straight through.
  if (window.location.pathname === '/authorize') {
    return <AuthorizeScreen />;
  }

  // Account activation / password reset from an emailed link — no session needed.
  if (window.location.pathname === '/reset') {
    return <ResetPasswordScreen />;
  }

  if (!auth.ready) {
    return <div className="login-shell"><div className="muted" style={{ margin: 'auto' }}>Inizializzazione…</div></div>;
  }
  if (!auth.authenticated) return <LoginScreen />;

  const isAdmin = auth.hasRole(Roles.ADMIN);
  const go = (p: string) => {
    const next = p as Page;
    if (PAGES.has(next)) navigate(next);
  };
  const userRole = isAdmin ? 'Amministratore' : 'Operatore';
  const destinations: Destination[] = COMMAND_DESTINATIONS
    .filter((d) => !d.adminOnly || isAdmin)
    .map((d) => ({ id: d.id, label: TITLES[d.id].t, crumb: TITLES[d.id].c, icon: d.icon }));

  return (
    <div className="app">
      {/* Kept mounted so both opening and closing can animate smoothly. */}
      <div
        className={`mobile-backdrop ${mobileOpen ? 'is-visible' : ''}`}
        onClick={() => setMobileOpen(false)}
        aria-hidden="true"
      />
      <Sidebar
        active={page}
        onNav={navigate}
        isAdmin={isAdmin}
        mobileOpen={mobileOpen}
        onCloseMobile={() => setMobileOpen(false)}
        userName={auth.name || auth.email}
        userRole={userRole}
        userInitials={initialsOf(auth.name, auth.email)}
        onOpenProfile={() => navigate('profile')}
        onOpenSettings={() => navigate('settings')}
      />
      <div className="main">
        <Topbar
          title={TITLES[page].t}
          crumb={TITLES[page].c}
          onMenu={() => setMobileOpen((open) => !open)}
          menuOpen={mobileOpen}
          onOpenSearch={() => setPaletteOpen(true)}
        />
        <div data-screen-label={page}>
          {page === 'hub' && <HubScreen onNav={go} />}
          {page === 'tools' && <ToolsScreen />}
          {page === 'infostaff' && <InfostaffScreen onNav={go} />}
          {page === 'notes' && <NotesScreen />}
          {page === 'wiki' && <WikiScreen />}
          {page === 'learning' && <LearningScreen />}
          {page === 'emails' && <EmailsScreen />}
          {page === 'tasks' && <TasksScreen />}
          {page === 'shifts' && <ShiftsScreen />}
          {page === 'calendar' && <CalendarScreen />}
          {page === 'leave' && <LeaveScreen />}
          {page === 'handover' && <HandoverScreen />}
          {page === 'admin' && isAdmin && <AdminScreen />}
          {page === 'profile' && <ProfileScreen />}
          {page === 'settings' && <SettingsScreen />}
        </div>
      </div>

      {/* Mobile bottom navigation — the daily-use destinations, one tap away. */}
      <MobileNav
        active={page}
        menuOpen={mobileOpen}
        onNav={navigate}
        onMore={() => setMobileOpen((open) => !open)}
      />

      <CommandPalette
        open={paletteOpen}
        destinations={destinations}
        onClose={() => setPaletteOpen(false)}
        onSelect={go}
      />
    </div>
  );
}

/** Fixed bottom bar shown only on phones. "Menu" opens the full drawer. */
function MobileNav({
  active, menuOpen, onNav, onMore,
}: {
  active: Page;
  menuOpen: boolean;
  onNav: (p: Page) => void;
  onMore: () => void;
}) {
  const { t } = useLang();
  const items: { id: Page; label: string; icon: IconName }[] = [
    { id: 'hub', label: 'Hub', icon: 'home' },
    { id: 'shifts', label: 'Turni', icon: 'calendar' },
    { id: 'tasks', label: 'Task', icon: 'list' },
    { id: 'learning', label: 'Corsi', icon: 'cap' },
  ];
  return (
    <nav className="mobile-nav" aria-label={t('Navigazione principale')}>
      {items.map((it) => (
        <button
          key={it.id}
          className={`mobile-nav__item ${active === it.id ? 'is-active' : ''}`}
          onClick={() => onNav(it.id)}
          aria-current={active === it.id ? 'page' : undefined}
        >
          <Icon name={it.icon} size={20} />
          <span>{t(it.label)}</span>
        </button>
      ))}
      <button
        className={`mobile-nav__item ${menuOpen ? 'is-active' : ''} ${!items.some((it) => it.id === active) ? 'has-context' : ''}`}
        onClick={onMore}
        aria-label={t('Apri tutte le sezioni')}
        aria-expanded={menuOpen}
        aria-controls="app-sidebar"
      >
        <Icon name="menu" size={20} />
        <span>Menu</span>
      </button>
    </nav>
  );
}
